// Kind of wrapper around the VueUse upload functionality and attachments Pinia store,
// because VueUse upload is not global and this composable give us the ability to reuse both in different components.
// For example "open" file dialog in dropdown.vue and actual upload in attachments.vue.

import { getUserData } from "@/auth/getUserData";
import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { useUploadAttachmentsStore } from "@/store/useUploadAttachmentsStore";
import { MediaTypeType } from "@/types/mediaType.type";
import { resolveFilesizeLimit } from "@/utils/channels";
import { ChatChannel } from "@/views/modules/messaging-inbox/useChat";
import { UploadDto } from "@saysimple-platform/dtos";
import { useFileDialog } from "@vueuse/core";
import { storeToRefs } from "pinia";

const supportedFileTypes = ["image/*", "application/*", "text/*", "video/*", "audio/*", ".pdf"];

const { open, onChange } = useFileDialog({
    accept: supportedFileTypes.join(","),
});

interface Options {
    // Ability to put the uploaded asset in the attachments store directly instead of manually pushing it. This will give you more flexibility
    useStore: boolean;
}

export default function useUpload(options: Options = { useStore: true }) {
    const uploadMultiple = async (
        attachments: File[] | null,
        channel: ChatChannel,
        path?: string
    ): Promise<UploadDto[]> => {
        if (!attachments) {
            return;
        }

        return await Promise.all(Array.from(attachments).map((attachment) => uploadSingle(attachment, channel, path)));
    };

    const uploadSingle = async (attachment: File, channel: ChatChannel, path?: string): Promise<UploadDto> => {
        if (!(attachment instanceof File)) {
            throw new Error(`Attachment is not an File instance.`);
        }

        if (!channel) {
            throw new Error("No channel provided.");
        }

        const { addAttachment } = useUploadAttachmentsStore();
        const { isUploadPending } = storeToRefs(useUploadAttachmentsStore());

        if (options.useStore) {
            isUploadPending.value = true;
        }

        const fileSizeLimit = resolveFilesizeLimit(channel.provider, channel.platform, attachment.type);

        //------------------------------------------------------------------

        if (attachment.size > 1048576 * fileSizeLimit) {
            const attachmentType = attachment.type.split("/")[0].toLowerCase();

            isUploadPending.value = false;
            throw new Error(
                i18n.t("entities.asset.filesize-error", {
                    filename: attachment.name,
                    size: fileSizeLimit,
                    filetype: i18n.t(
                        `entities.asset.file-types.${
                            supportedFileTypes.includes(attachmentType) ? attachmentType : "default"
                        }`
                    ),
                })
            );
        }

        //------------------------------------------------------------------

        const fileToCheck = new FormData();
        fileToCheck.append("file", attachment);
        fileToCheck.append("channel", JSON.stringify({ platform: channel.platform, provider: channel.provider }));

        const assetSupported = await axios
            .post(`${process.env.VUE_APP_API_GATEWAY}/assets/is-asset-supported`, fileToCheck, {
                // Convert size limit to mb
                maxBodyLength: 1048576 * fileSizeLimit,
                maxContentLength: 1048576 * fileSizeLimit,
            })
            .then((result) => result.data)
            .catch((error) => {
                isUploadPending.value = false;
                throw new Error("An error occurred while checking file compatibility");
            });

        if (!assetSupported) {
            isUploadPending.value = false;
            throw new Error(i18n.t("entities.asset.wrong-instance"));
        }

        //------------------------------------------------------------------

        const formData = new FormData();
        formData.append("file", attachment);

        //------------------------------------------------------------------

        return axios
            .post<UploadDto>(
                `${process.env.VUE_APP_API_GATEWAY}/assets/upload/storage-name/${getUserData().storageName}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    params: {
                        path: path,
                    },
                    // Convert size limit to mb
                    maxBodyLength: 1048576 * fileSizeLimit,
                    maxContentLength: 1048576 * fileSizeLimit,
                    // Uploading of assets can take a long time so timeout is 5 minutes
                    timeout: 300000,
                }
            )
            .then(({ data }) => {
                if (options.useStore) {
                    addAttachment(data);
                }

                return data;
            })
            .finally(() => (isUploadPending.value = false));
    };

    const getAllowedFileTypes = (
        mediaType: MediaTypeType,
        channel?: { platform: string; provider?: string }
    ): string[] => {
        let allowedImages: string[] = ["image/*"];
        let allowedVideo: string[] = ["video/*"];
        let allowedAudio: string[] = ["audio/*"];
        let allowedText: string[] = ["text/plain"];
        const allowedApplications: string[] = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-powerpoint",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        ];

        if (!mediaType && channel?.platform.toLowerCase() === "instagram") {
            return [allowedImages, allowedVideo].flat();
        } else if (
            channel?.platform.toLowerCase() === "whatsapp" &&
            (channel?.provider?.toLowerCase() === "messagebird" || channel?.provider?.toLowerCase() === "bird")
        ) {
            allowedVideo = ["video/mp4"];
            allowedAudio = ["audio/aac", "audio/mp4", "audio/mpeg", "audio/amr", "audio/ogg"];
            allowedImages = ["image/jpeg", "image/png"];
        } else if (channel?.platform.toLowerCase() === "whatsapp" && channel.provider?.toLowerCase() === "spryng") {
            allowedVideo = ["video/mp4", "video/3gp"];
            allowedAudio = ["audio/aac", "audio/mp4", "audio/mpeg", "audio/amr", "audio/ogg", "audio/opus"];
            allowedImages = ["image/jpeg", "image/png"];
            allowedText = ["text/plain"];
        }

        switch (mediaType) {
            case MediaTypeType.IMAGE:
                return allowedImages;
            case MediaTypeType.VIDEO:
                return allowedVideo;
            case MediaTypeType.DOCUMENT:
                return [allowedApplications, allowedText].flat();
            default:
                return [allowedImages, allowedVideo, allowedAudio, allowedApplications, allowedText].flat();
        }
    };

    return {
        open,
        onChange,
        uploadSingle,
        uploadMultiple,
        getAllowedFileTypes,
    };
}
